import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScreenHeader } from "../../next/page/ScreenHeader";
import { FormAlert, TableKit } from "@NP-Webber/crm-kit";
import { toClientApiError } from "../../api/httpClient";
import {
  settingsPermissionsApi,
  type TenantRole,
} from "../../api/settingsPermissionsApi";
import { useTableLayoutPrefs } from "../../hooks/useTableLayoutPrefs";
import { UiStateMessage } from "../../engines";
import type { UiLoadState } from "../../engines";

const COLUMNS = [
  {
    key: "name",
    title: "שם",
    sortable: true,
    filterable: true,
    minWidth: 140,
  },
  {
    key: "key",
    title: "מפתח",
    sortable: true,
    filterable: true,
    minWidth: 160,
  },
  {
    key: "isSystemLabel",
    title: "סוג",
    filterable: true,
    minWidth: 100,
  },
  {
    key: "permissionCount",
    title: "מספר הרשאות",
    sortable: true,
    minWidth: 120,
  },
];

export function SettingsPermissionsPage() {
  const navigate = useNavigate();
  const { prefs, savePrefs } = useTableLayoutPrefs("settings.roles.list");
  const [roles, setRoles] = useState<TenantRole[]>([]);
  const [state, setState] = useState<UiLoadState>("loading");
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    const load = async () => {
      setState("loading");
      setError(undefined);
      try {
        const items = await settingsPermissionsApi.listRoles();
        setRoles(items);
        setState(items.length === 0 ? "empty" : "ready");
      } catch (err) {
        const apiError = toClientApiError(err);
        if (apiError.status === 403) {
          setState("permission");
        } else {
          setError(apiError.message);
          setState("error");
        }
      }
    };
    void load();
  }, []);

  if (state === "loading" || state === "error" || state === "permission") {
    return (
      <section className="page-section page-section--list" aria-label="הרשאות">
        <UiStateMessage state={state} errorMessage={error} />
      </section>
    );
  }

  const tableRows = roles.map((role) => ({
    ...role,
    id: role.id,
    isSystemLabel: role.isSystem ? "מערכתי" : "מותאם",
    permissionCount: role.permissions.length,
  }));

  return (
    <section className="page-section page-section--list" aria-label="הרשאות">
      <ScreenHeader
        title="הרשאות ותבניות תפקיד"
        sub="מה כל תפקיד רואה ועושה — לפי מסך ופעולה"
        more={[{ key: "back", label: "חזרה להגדרות", icon: "right", onClick: () => navigate("/settings") }]}
      />
      {state === "empty" ? (
        <FormAlert severity="info">לא נמצאו תבניות תפקיד בארגון.</FormAlert>
      ) : null}
      <div className="page-section__table-surface">
        <TableKit
          columns={COLUMNS}
          data={tableRows as unknown as Array<Record<string, unknown>>}
          loading={false}
          clientSideMode
          emptyMessage="אין תבניות תפקיד."
          exportFileName="roles"
          showExport
          showFilters
          showColumnPicker
          showAdvancedSettings
          columnOrder={(prefs.columnOrder as string[] | undefined) ?? undefined}
          onColumnOrderChange={(next: string[]) => {
            void savePrefs({ ...prefs, columnOrder: next });
          }}
          hiddenColumnKeys={(prefs.hiddenColumnKeys as string[] | undefined) ?? undefined}
          onHiddenColumnKeysChange={(next: string[]) => {
            void savePrefs({ ...prefs, hiddenColumnKeys: next });
          }}
        />
      </div>
    </section>
  );
}
